/**
 * Servicio para guardar conversaciones de Galia 1.5 en Supabase.
 *
 * Historia: Galia 1.0 escribia via Lambda FinalTasksAF (proxy a Supabase). Esa
 * Lambda dio problemas en produccion y se quito; ahora el frontend escribe
 * directo a supabaseService. Las referencias a mentoraibot.com son del flujo
 * antiguo de tracking de tiempo, tambien eliminadas (ver interactionTimeService).
 */

import { ConversationData, Conversation, UserSummary, UserCharacterization } from '../types';
import configService from './configService';
import interactionTimeService from './interactionTimeService';
import supabaseService from './supabaseService';
import authService from './authService';
import gamificationService from './gamificationService';
import { isTestingBuild } from '../utils/featureFlags';

// Declaración para función JavaScript externa (si existe)
declare global {
  interface Window {
    LambdaFunction?: (url: string, jsonData: string) => void;
  }
}

export class ConversationSaverService {
  private internalInteractionId: string = '';
  private numeroMensajesToSend: number = 0;
  private currentMode: string | null = null;
  private currentPersonality: string | null = null;
  private currentAccent: string | null = null;
  private textMessageCount: number = 0;
  private audioMessageCount: number = 0;
  private notValidMessageCount: number = 0; // Contador de mensajes no válidos (vacíos)
  
  // Rastreo de tiempo del chat (abierto/cerrado)
  private chatOpenStartTime: number | null = null;
  private chatClosedStartTime: number | null = null;
  private totalChatOpenTime: number = 0; // Tiempo acumulado con chat abierto (time_text)
  private totalChatClosedTime: number = 0; // Tiempo acumulado con chat cerrado (time_no_text)
  private isChatCurrentlyOpen: boolean = false;

  // Rastreo de tiempo muerto (después de que el asistente termina de hablar)
  private idleTimer: NodeJS.Timeout | null = null; // Timer de 15 segundos antes de empezar a contar
  private idleStartTime: number | null = null; // Timestamp cuando empezó el tiempo muerto (después de 15s)
  private totalIdleTime: number = 0; // Tiempo muerto acumulado en segundos
  private readonly IDLE_THRESHOLD = 15; // Segundos de espera antes de empezar a contar tiempo muerto

  // Baseline de time_total al retomar una conversación (< 24h): segundos ya
  // persistidos en la fila de Supabase por sesiones anteriores. Se suma SOLO al
  // armar el campo time_total del upsert (estructuraToSend) — nunca dentro de
  // interactionTimeService.getTimeElapsed(), porque gamificationService suma
  // getTimeElapsed() al total_time_seconds del usuario y doble-contaría.
  private resumedTimeTotalBaseline: number = 0;

  // Segundos de interactionTimeService.getTimeElapsed() que ya se sumaron a
  // user_summary.total_time_seconds en guardados anteriores de ESTA sesión de
  // app. updateUserSummary suma solo el delta desde el último guardado; sin
  // esto, cada guardado (cada 30s + cada turno) re-sumaba la sesión completa y
  // total_time_seconds crecía cuadráticamente (se encontraron usuarios con 622h
  // acumuladas frente a ~3h reales). El reloj es global a la sesión de app
  // (monotónico), así que NO se resetea en resetConversationState.
  private summarizedSessionSeconds: number = 0;

  constructor() {
    // Generar ID único para esta interacción
    this.internalInteractionId = this.generateGuid();
  }

  /**
   * Establecer configuración actual del usuario (modo, personalidad, acento)
   */
  setCurrentConfiguration(mode: string, personality: string, accent: string): void {
    this.currentMode = mode;
    this.currentPersonality = personality;
    this.currentAccent = accent;
  }

  /**
   * Obtener el ID de la interacción actual
   */
  getCurrentInteractionId(): string {
    return this.internalInteractionId;
  }

  /**
   * Obtener el nombre del bot según el modo
   */
  private getBotName(mode: string | null): string {
    if (!mode) return 'galia';
    
    const nameMap: Record<string, string> = {
      'conversacional': 'galia',
      'estudio': 'galia',
      'examen': 'galia',
      'actividades-docente': 'galia',
      'actividades-estudiante': 'galia',
    };
    
    return nameMap[mode] || 'galia';
  }

  /**
   * Incrementar contador de mensajes de texto
   */
  incrementTextCount(): void {
    this.textMessageCount++;
  }

  /**
   * Incrementar contador de mensajes de audio
   */
  incrementAudioCount(): void {
    this.audioMessageCount++;
  }

  /**
   * Incrementar contador de mensajes no válidos (vacíos)
   */
  incrementNotValidCount(): void {
    this.notValidMessageCount++;
  }

  /**
   * Resetear estado de conversación para nueva interacción
   * Genera nuevo internalInteractionId y resetea contadores
   */
  resetConversationState(): void {
    this.internalInteractionId = this.generateGuid();
    this.numeroMensajesToSend = 0;
    this.textMessageCount = 0;
    this.audioMessageCount = 0;
    this.notValidMessageCount = 0;
    // Resetear tiempo muerto
    this.stopIdleTimer();
    this.totalIdleTime = 0;
    // Conversación nueva: sin tiempo previo acumulado.
    this.resumedTimeTotalBaseline = 0;
  }

  /**
   * Retomar una conversación previa (< 24h de inactividad).
   * Reusa el internal_interaction_id existente para que el próximo guardarData
   * actualice (UPDATE) la fila en Supabase en vez de crear una nueva, y marca
   * cuántos mensajes ya están persistidos (numeroMensajesToSend) para no
   * reenviar el historial entero como "mensajes nuevos".
   *
   * Los contadores de tiempo (text/audio/idle) se mantienen en 0 porque
   * representan tiempo de la SESIÓN actual, no de la conversación acumulada.
   * El time_total previo de la fila entra como baseline y se suma solo al armar
   * el campo time_total del upsert — sin él, el upsert sobrescribiría el
   * acumulado con únicamente el tiempo de esta sesión.
   */
  restoreConversationState(
    internalInteractionId: string,
    alreadyPersistedMessageCount: number,
    previousTimeTotalSeconds: number = 0,
  ): void {
    if (!internalInteractionId) return;
    this.internalInteractionId = internalInteractionId;
    this.numeroMensajesToSend = Math.max(0, alreadyPersistedMessageCount);
    this.resumedTimeTotalBaseline = Math.max(0, Math.round(previousTimeTotalSeconds));
    // No tocar text/audio/notValid counts: son de la sesión actual.
    // No tocar timers: el tiempo de chat abierto/cerrado/idle se calcula desde que
    // se reabrió la app, no desde el inicio de la conversación.
  }

  /**
   * Registrar cambio de visibilidad del chat
   * @param isVisible true si el chat se abrió, false si se cerró
   */
  setChatVisibility(isVisible: boolean): void {
    const now = Date.now();
    
    if (isVisible && !this.isChatCurrentlyOpen) {
      // Chat se acaba de abrir
      // Si había estado cerrado, acumular ese tiempo
      if (this.chatClosedStartTime !== null) {
        const closedDuration = Math.floor((now - this.chatClosedStartTime) / 1000); // en segundos
        this.totalChatClosedTime += closedDuration;
        this.chatClosedStartTime = null;
      }
      this.chatOpenStartTime = now;
      this.isChatCurrentlyOpen = true;
    } else if (!isVisible && this.isChatCurrentlyOpen) {
      // Chat se acaba de cerrar
      // Acumular el tiempo que estuvo abierto
      if (this.chatOpenStartTime !== null) {
        const openDuration = Math.floor((now - this.chatOpenStartTime) / 1000); // en segundos
        this.totalChatOpenTime += openDuration;
        this.chatOpenStartTime = null;
      }
      this.chatClosedStartTime = now;
      this.isChatCurrentlyOpen = false;
    }
  }

  /**
   * Obtener tiempo total con chat abierto (time_text)
   */
  getTimeText(): number {
    let currentOpenTime = 0;
    if (this.isChatCurrentlyOpen && this.chatOpenStartTime !== null) {
      // Si el chat está abierto ahora, agregar el tiempo desde que se abrió
      currentOpenTime = Math.floor((Date.now() - this.chatOpenStartTime) / 1000);
    }
    return this.totalChatOpenTime + currentOpenTime;
  }

  /**
   * Obtener tiempo total con chat cerrado (time_no_text)
   */
  getTimeNoText(): number {
    let currentClosedTime = 0;
    if (!this.isChatCurrentlyOpen && this.chatClosedStartTime !== null) {
      // Si el chat está cerrado ahora, agregar el tiempo desde que se cerró
      currentClosedTime = Math.floor((Date.now() - this.chatClosedStartTime) / 1000);
    }
    return this.totalChatClosedTime + currentClosedTime;
  }

  /**
   * Iniciar timer de tiempo muerto cuando el asistente termina de responder
   * Espera 15 segundos y luego empieza a contar tiempo muerto
   */
  startIdleTimer(): void {
    // Limpiar timer anterior si existe
    this.stopIdleTimer();


    // Iniciar timer de 15 segundos
    this.idleTimer = setTimeout(() => {
      // Han pasado 15 segundos sin que el usuario hable
      this.idleStartTime = Date.now();
    }, this.IDLE_THRESHOLD * 1000);
  }

  /**
   * Detener el contador de tiempo muerto cuando el usuario envía un mensaje
   */
  stopIdleTimer(): void {
    // Limpiar timer de espera si existe
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    // Si estaba contando tiempo muerto, acumularlo y detener
    if (this.idleStartTime !== null) {
      const idleDuration = Math.floor((Date.now() - this.idleStartTime) / 1000);
      this.totalIdleTime += idleDuration;
      this.idleStartTime = null;
    }
  }

  /**
   * Obtener tiempo muerto acumulado (en segundos)
   * Incluye el tiempo actual si está contando
   */
  getIdleTime(): number {
    let currentIdleTime = 0;
    if (this.idleStartTime !== null) {
      // Si está contando tiempo muerto ahora, agregar el tiempo desde que empezó
      currentIdleTime = Math.floor((Date.now() - this.idleStartTime) / 1000);
    }
    return this.totalIdleTime + currentIdleTime;
  }

  /**
   * Concatenar mensajes assistant consecutivos
   * Concatena chunks de la misma respuesta normalmente
   * EXCEPTO cuando un mensaje tiene afterInvalidMessage: true (viene después de un mensaje no válido)
   */
  private concatenateAssistantMessages(messages: Array<{ role: string; content: string; afterInvalidMessage?: boolean }>): Array<{ role: string; content: string }> {
    return messages.reduce((acc: Array<{ role: string; content: string }>, message, index) => {
      if (index === 0) {
        // Primer mensaje siempre se agrega
        acc.push({ role: message.role, content: message.content });
      } else {
        const previousMessage = acc[acc.length - 1];
        
        // Si ambos son assistant, verificar si deben concatenarse
        if (message.role === 'assistant' && previousMessage.role === 'assistant') {
          // Si el mensaje actual viene después de un mensaje no válido, NO concatenar
          if (message.afterInvalidMessage) {
            // Es una nueva respuesta después de un mensaje no válido
            acc.push({ role: message.role, content: message.content });
          } else {
            // Concatenar normalmente (chunks de la misma respuesta)
            previousMessage.content = `${previousMessage.content}\n\n${message.content}`;
          }
        } else {
          // Si es user o es el primer assistant después de un user, agregar normalmente
          acc.push({ role: message.role, content: message.content });
        }
      }
      return acc;
    }, []);
  }

  /**
   * Guardar datos de conversación
   */
  async guardarData(finalMessages: Array<{ role: string; content: string; afterInvalidMessage?: boolean }>): Promise<void> {
    if (finalMessages.length <= this.numeroMensajesToSend) {
      return; // No hay nuevos mensajes para enviar
    }
    // Loop 21/09/2026 (fnd_ba3bd48eb0): filas con CERO mensajes del estudiante y
    // una única respuesta de Galia — la respuesta que seguía llegando por el WS
    // cuando el estudiante ya había abierto conversación nueva, guardada bajo el
    // internal_interaction_id nuevo. En 24 h: 3 filas de 67. Inflan el KPI de
    // conversaciones, parten la memoria y en la pestaña Seguimiento aparecen
    // respuestas sin pregunta. Una conversación sin ningún turno del estudiante
    // no tiene nada que persistir: se espera al primero. No se toca
    // numeroMensajesToSend, así que el guardado se hará completo en cuanto llegue.
    if (!finalMessages.some((m) => m.role === 'user')) {
      return;
    }

    try {
      // Concatenar mensajes assistant consecutivos antes de procesar
      const processedMessages = this.concatenateAssistantMessages(finalMessages);
      
      // IMPORTANTE: Enviar TODO el historial completo, no solo los mensajes nuevos
      // Esto asegura que cuando Lambda actualice la conversación, tenga todo el historial
      const listaMensajesCompleta = processedMessages
        .map((message) => ({
          [message.role]: message.content,
        }));

      // Solo los mensajes nuevos para procesamiento (si es necesario)
      const listaMensajesNuevos = processedMessages
        .slice(this.numeroMensajesToSend)
        .map((message) => ({
          [message.role]: message.content,
        }));

      if (listaMensajesNuevos.length > 0) {
        // Enviar TODO el historial completo, no solo los nuevos
        await this.estructuraToSend(listaMensajesCompleta);
        this.numeroMensajesToSend = finalMessages.length;
        
        // NO registrar interacciones aquí - total_interactions cuenta conversaciones, no interacciones individuales
        // El conteo de conversaciones se hace en updateUserSummary cuando se guarda la conversación
      }
    } catch (error) {
      console.error('Error al guardar datos:', error);
    }
  }

  private async estructuraToSend(
    listaMensajesEnviar: Array<{ [key: string]: string }>
  ): Promise<void> {
    const config = configService.getConfig();
    // time_total = baseline de sesiones anteriores de esta conversación (si se
    // retomó) + tiempo de la sesión actual. El baseline se suma aquí y no en
    // getTimeElapsed() para no doble-contar en gamificación (ver
    // resumedTimeTotalBaseline).
    const sessionTime = Math.round(interactionTimeService.getTimeElapsed());
    const totalTime = this.resumedTimeTotalBaseline + sessionTime;
    // const updateTimeID = interactionTimeService.getUpdateTimeID() || '';

    // Usar firstname y lastname de Apolearn si están disponibles, sino parsear de fullName
    let firstname = config.userFirstname;
    let lastname = config.userLastname;
    
    // Si no vienen de Apolearn, intentar parsear de fullName
    if (!firstname && !lastname && config.userFullName) {
      const parsed = this.parseFullName(config.userFullName);
      firstname = parsed.firstname;
      lastname = parsed.lastname;
    }
    
    // Generar fullName como suma de firstname + lastname
    const fullName = firstname && lastname 
      ? `${firstname} ${lastname}` 
      : firstname || config.userFullName || '';

    // Obtener contexto de Apolearn si está disponible
    const apolearnData = authService.getApolearnUserData();
    let apolearnContext: {
      apolearn_user_id?: number;
      apolearn_module_id?: number;
      apolearn_classroom_id?: number;
      apolearn_activity_id?: number;
    } = {};

    if (apolearnData) {
      apolearnContext.apolearn_user_id = apolearnData.user.id;

      // classroom_id viene del context_id de la URL (LTI/iframe de Apolearn).
      // Apolearn envía context_id=<classroom_id> cuando lanza el módulo.
      const contextId = authService.getContextId();
      if (contextId) {
        const classroomIdNum = parseInt(contextId, 10);
        if (!isNaN(classroomIdNum) && classroomIdNum > 0) {
          apolearnContext.apolearn_classroom_id = classroomIdNum;
        }
      }

      // module_id y activity_id (tache_id) no vienen actualmente en el URL de Apolearn,
      // solo cuando el estudiante accede vía "actividad de docente" con tache_id.
    }

    // Construir el objeto Conversation que se persistira en Supabase. Mapea
    // 1:1 los campos del schema (src/types/index.ts:330). Los campos del payload
    // viejo de Lambda que NO existen en el schema (time_text, bot_name, canal,
    // interactions_not_valid_count) se omiten — eran para uso interno de la
    // Lambda y no se persistian en la tabla.
    const conversation: Partial<Conversation> = {
      internal_interaction_id: this.internalInteractionId,
      student_id: config.userID || undefined,
      student_email: config.email || '',
      student_firstname: firstname || undefined,
      student_lastname: lastname || undefined,
      student_name: fullName || undefined,
      assistant_id: config.assistantID || undefined,
      mode: this.currentMode || undefined,
      personality: this.currentPersonality || undefined,
      accent: this.currentAccent || undefined,
      questions: listaMensajesEnviar.map((msg) => {
        const role = Object.keys(msg)[0];
        const content = Object.values(msg)[0];
        return {
          role: (role === 'user' || role === 'assistant') ? role : ('user' as const),
          content,
        };
      }),
      total_messages_count: listaMensajesEnviar.length,
      interactions_text_count: this.textMessageCount,
      interactions_audio_count: this.audioMessageCount,
      time_total: totalTime,
      time_no_text: this.getTimeNoText(),
      time_dead: this.getIdleTime(),
      gamification_points_section: 0,
      available: true,
      environment: isTestingBuild() ? 'dev' : 'prod',
      ...apolearnContext,
    };

    // Fire-and-forget: el guardado no debe bloquear el flujo del chat.
    this.saveToSupabase(conversation).catch((error) => {
      console.error('[ConversationSaver] Error guardando en Supabase:', error);
    });
  }

  /**
   * Persiste la conversacion en Supabase + actualiza user_summary + reescribe
   * questions con timestamps (TTFT). Reemplaza el viejo sendToLambdaAndProcessResponse.
   */
  private async saveToSupabase(conversation: Partial<Conversation>): Promise<void> {
    const saved = await supabaseService.saveConversation(conversation);
    if (!saved) {
      console.warn('[ConversationSaver] saveConversation devolvio null; abortando post-save');
      return;
    }

    const config = configService.getConfig();
    if (!config.email) return;

    const interactionId = saved.internal_interaction_id || conversation.internal_interaction_id;

    // user_summary acumulativo (total_interactions, total_time_seconds, etc.).
    // NOTA: hereda el bug preexistente de incrementar total_interactions en cada
    // save (cada 30s + cada turno). Fix separado.
    try {
      await this.updateUserSummary(config.email, conversation);
    } catch (err) {
      console.error('[ConversationSaver] Error en updateUserSummary:', err);
    }

    // Apolearn context: aunque saveConversation ya incluye estos campos en el
    // upsert, mantenemos el update explicito por compatibilidad con la logica
    // previa (es idempotente).
    if (interactionId) {
      try {
        await supabaseService.updateConversationApolearnContext(interactionId, {
          apolearn_user_id: conversation.apolearn_user_id,
          apolearn_classroom_id: conversation.apolearn_classroom_id,
          apolearn_module_id: conversation.apolearn_module_id,
          apolearn_activity_id: conversation.apolearn_activity_id,
        });
      } catch (err) {
        console.error('[ConversationSaver] Error en updateConversationApolearnContext:', err);
      }
    }

    // Enriquecer questions con timestamps por mensaje (ts del render + receivedAt
    // del primer chunk assistant — clave para metricas TTFT/latencia).
    // Import dinamico para evitar ciclo webSocketService <-> conversationSaverService.
    if (interactionId) {
      try {
        const { default: webSocketService } = await import('./webSocketService');
        // Loop 26/10/2026 (fnd_c58fb24325): el guardado es fire-and-forget y
        // este punto llega tras varios await. Si mientras tanto el estudiante
        // cambió de pestaña (switchConversation) o abrió conversación nueva, el
        // historial del chat ya es el de OTRA conversación y se escribía bajo
        // este interactionId: la fila nueva de consbg@ del 25/10 quedó con los
        // 190 mensajes del 26/09 y la sesión del día se perdió. Si el hilo
        // activo ya no es el que se guardó, no se reescribe questions.
        const firstUser = (list: Array<{ role?: string; content?: string }> | undefined) =>
          (list || []).find((m) => m.role === 'user')?.content;
        if (
          this.internalInteractionId !== conversation.internal_interaction_id ||
          firstUser(webSocketService.getChatHistory()) !== firstUser(conversation.questions as Array<{ role?: string; content?: string }>)
        ) {
          console.warn('[Saver] questions desfasadas: el chat ya es otra conversación, no se reescriben', {
            saved: interactionId,
            active: this.internalInteractionId,
          });
          return;
        }
        const chatHistory = webSocketService.getChatHistory();
        const enrichedQuestions = chatHistory
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => {
            const entry: {
              role: 'user' | 'assistant';
              content: string;
              ts?: number;
              receivedAt?: number;
            } = {
              role: m.role as 'user' | 'assistant',
              content: m.content,
            };
            if (typeof m.timestamp === 'number') entry.ts = m.timestamp;
            if (typeof m.receivedAt === 'number') entry.receivedAt = m.receivedAt;
            return entry;
          });

        if (enrichedQuestions.length > 0) {
          await supabaseService.updateConversationQuestionsWithTimestamps(
            interactionId,
            enrichedQuestions,
          );
        }
      } catch (err) {
        console.error('[ConversationSaver] Error enriqueciendo questions con timestamps:', err);
      }
    }
  }


  /**
   * Parsear nombre completo en firstname y lastname
   * Si no hay apellido, lastname será vacío
   */
  private parseFullName(fullName: string | undefined): { firstname: string; lastname: string } {
    if (!fullName || fullName.trim() === '') {
      return { firstname: '', lastname: '' };
    }
    
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstname: parts[0], lastname: '' };
    }
    
    // Primera parte es el nombre, el resto es el apellido
    const firstname = parts[0];
    const lastname = parts.slice(1).join(' ');
    
    return { firstname, lastname };
  }

  /**
   * Guardar conversación en Supabase
   * DEPRECATED: Ya no se usa. Lambda guarda en Supabase directamente.
   */
  // @ts-ignore - Función deprecated, no se usa
  private async saveToSupabase_DEPRECATED(
    questions: ConversationData,
    finalMessages: Array<{ [key: string]: string }>
  ): Promise<void> {
    try {
      const config = configService.getConfig();
      if (!config.email) {
        return;
      }

      // Usar firstname y lastname de Apolearn si están disponibles, sino parsear de fullName
      let firstname = config.userFirstname;
      let lastname = config.userLastname;
      
      // Si no vienen de Apolearn, intentar parsear de fullName
      if (!firstname && !lastname && config.userFullName) {
        const parsed = this.parseFullName(config.userFullName);
        firstname = parsed.firstname;
        lastname = parsed.lastname;
      }
      
      // Generar fullName como suma de firstname + lastname
      const fullName = firstname && lastname 
        ? `${firstname} ${lastname}` 
        : firstname || config.userFullName || '';

      // Obtener contexto de Apolearn si está disponible
      const apolearnData = authService.getApolearnUserData();
      let apolearnContext: {
        apolearn_user_id?: number;
        apolearn_module_id?: number;
        apolearn_classroom_id?: number;
        apolearn_activity_id?: number;
      } = {};

      if (apolearnData) {
        apolearnContext.apolearn_user_id = apolearnData.user.id;
        // TODO: Obtener module_id, classroom_id, activity_id del contexto actual si está disponible
        // Por ahora solo guardamos el user_id
      }

      // Convertir final_messages al formato questions (JSONB)
      const questionsArray = finalMessages.map((msg) => {
        const role = Object.keys(msg)[0];
        const content = Object.values(msg)[0];
        return { role, content };
      });

      // Contar mensajes por tipo
      // Preparar conversación para Supabase
      const conversation: Partial<Conversation> = {
        internal_interaction_id: this.internalInteractionId,
        student_id: config.userID || undefined,
        student_email: config.email,
        student_firstname: firstname || undefined,
        student_lastname: lastname || undefined,
        student_name: fullName || undefined, // Mantener por compatibilidad
        assistant_id: config.assistantID || undefined,
        mode: this.currentMode || undefined,
        personality: this.currentPersonality || undefined,
        accent: this.currentAccent || undefined,
        questions: questionsArray.map(m => ({
          role: (m.role === 'user' || m.role === 'assistant') ? m.role : 'user' as 'user' | 'assistant',
          content: m.content
        })),
        total_messages_count: questionsArray.length,
        interactions_text_count: this.textMessageCount,
        interactions_audio_count: this.audioMessageCount,
        time_total: questions.time_total,
        time_no_text: questions.time_no_text,
        gamification_points_section: questions.gamification_points_section || 0,
        ...apolearnContext,
        available: questions.available !== false,
      };

      // Guardar en Supabase
      await supabaseService.saveConversation(conversation);

      // Actualizar resumen acumulativo
      await this.updateUserSummary(config.email, conversation);
    } catch (error) {
      console.error('[ConversationSaver] Error al guardar en Supabase:', error);
      // No lanzar error para no interrumpir el flujo de Lambda
    }
  }

  /**
   * Actualizar resumen acumulativo del usuario
   */
  private async updateUserSummary(
    email: string,
    conversation: Partial<Conversation>
  ): Promise<void> {
    try {
      // Obtener resumen actual o crear uno nuevo
      let summary = await supabaseService.getUserSummary(email);
      
      if (!summary) {
        summary = {
          email,
          total_time_seconds: 0,
          total_interactions: 0,
          total_points: 0,
          current_level: 1,
          current_streak: 0,
          unlocked_rewards: [],
          interactions_by_mode: {},
        };
      }

      // Actualizar campos acumulativos
      const previousInteractions = summary.total_interactions || 0;
      const newInteractions = previousInteractions + 1;

      // Sumar SOLO el delta de tiempo activo desde el último guardado. Se lee
      // el reloj directo (y no conversation.time_total - baseline) porque
      // getTimeElapsed() es monotónico en toda la sesión de app, incluso si el
      // usuario cambia de conversación (resetConversationState) o retoma otra
      // (restoreConversationState cambia el baseline).
      const elapsedNow = Math.round(interactionTimeService.getTimeElapsed());
      const sessionTimeSeconds = Math.max(0, elapsedNow - this.summarizedSessionSeconds);
      this.summarizedSessionSeconds = Math.max(this.summarizedSessionSeconds, elapsedNow);

      const updates: Partial<UserSummary> = {
        total_time_seconds: (summary.total_time_seconds || 0) + sessionTimeSeconds,
        total_interactions: newInteractions,
        total_points: (summary.total_points || 0) + (conversation.gamification_points_section || 0),
        last_interaction_date: new Date().toISOString(),
      };

      // Actualizar first_interaction_date si es la primera vez
      if (!summary.first_interaction_date) {
        updates.first_interaction_date = new Date().toISOString();
      }

      // NOTA: NO actualizar current_streak aquí porque:
      // 1. La racha se actualiza solo cuando se registra la conexión diaria (InitialScreen)
      // 2. La racha es independiente de las conversaciones - solo depende de si el usuario se conectó hoy
      // 3. Actualizar aquí causaría que la racha se recalcule innecesariamente en cada conversación

      // Actualizar nivel basado en tiempo e interacciones
      try {
        const levelProgress = await gamificationService.getLevelProgress(email);
        if (levelProgress) {
          updates.current_level = levelProgress.currentLevel;
        }
      } catch (levelError) {
        console.error('[ConversationSaver] Error al actualizar nivel:', levelError);
        // Si falla, mantener el nivel actual
        updates.current_level = summary.current_level || 1;
      }

      // Actualizar interactions_by_mode
      if (conversation.mode) {
        const modeCounts = summary.interactions_by_mode || {};
        modeCounts[conversation.mode] = (modeCounts[conversation.mode] || 0) + 1;
        updates.interactions_by_mode = modeCounts;

        // Actualizar preferred_mode si este modo tiene más interacciones
        const maxMode = Object.entries(modeCounts).reduce((a, b) => 
          modeCounts[a[0]] > modeCounts[b[0]] ? a : b
        );
        updates.preferred_mode = maxMode[0];
      }
      
      // Actualizar campos de comportamiento en user_characterization (diccionario flexible)
      const currentChar = summary.user_characterization || {};
      const updatedChar: UserCharacterization = { ...currentChar };
      
      // Actualizar preferred_modes (usar el actualizado en updates)
      if (conversation.mode) {
        updatedChar.preferred_modes = updates.interactions_by_mode || summary.interactions_by_mode || {};
      }
      
      // Actualizar preferred_personality
      if (conversation.personality) {
        updatedChar.preferred_personality = conversation.personality;
      }
      
      // Actualizar preferred_accent
      if (conversation.accent) {
        updatedChar.preferred_accent = conversation.accent;
      }
      
      // Calcular duración promedio de sesión
      const totalInteractions = (summary.total_interactions || 0) + 1;
      const totalTime = (summary.total_time_seconds || 0) + sessionTimeSeconds;
      if (totalInteractions > 0 && totalTime > 0) {
        updatedChar.average_session_duration = Math.round(totalTime / totalInteractions);
      }
      
      // Calcular ratio texto/audio
      const textCount = conversation.interactions_text_count || 0;
      const audioCount = conversation.interactions_audio_count || 0;
      if (textCount + audioCount > 0) {
        updatedChar.text_vs_audio_ratio = textCount / (textCount + audioCount);
      }

      // Persistir el rol (profesor/estudiante) para segmentación en el dashboard.
      // FUENTE DE VERDAD = dominio del correo institucional de la AF (mismo
      // criterio que el backend generate-token y el gating del modo profesor en
      // InitialScreen). Antes se usaba el rol LTI, poco confiable: la resolución
      // async de Apolearn lo degrada a 'student', por eso profesoras como
      // patricia.pulido@alianzafrancesa.edu.co salían como estudiante en el
      // dashboard (y cada sesión sobrescribía el backfill). En dev se respeta
      // ?force_role= para QA.
      const isTestingPhase = configService.getConfig().testingPhase ?? false;
      updatedChar.profile = {
        ...(currentChar.profile || {}),
        role: authService.getEffectiveRole(email, isTestingPhase),
        raw_role: authService.getUserRole() ?? null,
        source: 'email_domain',
        synced_at: new Date().toISOString(),
      };

      // Actualizar user_characterization (mantener todos los campos existentes + nuevos)
      updates.user_characterization = updatedChar;

      // Actualizar datos de Apolearn si están disponibles
      const apolearnData = authService.getApolearnUserData();
      if (apolearnData) {
        updates.apolearn_user_id = apolearnData.user.id;

        // Calcular total_timespent y progress_by_classroom desde estadísticas
        if (apolearnData.classrooms.length > 0) {
          let totalTimespent = 0;
          const timespentByClassroom: Record<string, number> = {};
          const progressByClassroom: Record<string, number> = {};

          apolearnData.classrooms.forEach((classroom) => {
            if (classroom.statistics) {
              totalTimespent += classroom.statistics.timespent;
              timespentByClassroom[classroom.id.toString()] = classroom.statistics.timespent;
              progressByClassroom[classroom.id.toString()] = classroom.statistics.progress;
            }
          });

          updates.apolearn_total_timespent = totalTimespent;
          updates.apolearn_timespent_by_classroom = timespentByClassroom;
          
          // Calcular promedio de progress
          const progressValues = Object.values(progressByClassroom);
          if (progressValues.length > 0) {
            const avgProgress = progressValues.reduce((a, b) => a + b, 0) / progressValues.length;
            updates.apolearn_average_progress = avgProgress;
          }
          updates.apolearn_progress_by_classroom = progressByClassroom;
        }
      }

      // Guardar actualizaciones
      await supabaseService.saveOrUpdateUserSummary({
        ...summary,
        ...updates,
      });
    } catch (error) {
      console.error('[ConversationSaver] Error al actualizar user_summary:', error);
    }
  }

  /**
   * Actualizar conversación con resultados de Lambda (grade, insights, summary)
   * DEPRECATED: Ya no se usa. Lambda guarda en Supabase directamente.
   */
  async updateWithLambdaResults_DEPRECATED(
    grade?: string,
    insights?: string,
    summary?: string
  ): Promise<void> {
    try {
      await supabaseService.updateConversationWithLambdaResults(
        this.internalInteractionId,
        { grade, insights, summary }
      );
      
      // Actualizar caracterización del usuario si hay insights
      if (insights) {
        await this.updateUserCharacterization(insights);
      }
    } catch (error) {
      console.error('[ConversationSaver] Error al actualizar con resultados de Lambda:', error);
    }
  }
  
  /**
   * Consolidar insights de Lambda en la caracterización del usuario
   * Usa un diccionario flexible que permite agregar campos parametrizables
   */
  private async consolidateUserCharacterization(
    insights: string,
    currentChar: UserCharacterization | undefined
  ): Promise<UserCharacterization> {
    const char: UserCharacterization = currentChar || {};
    const metadata = char._metadata || {};
    
    // Parsear insights (texto en francés)
    // Formato esperado:
    // - Méthode d'apprentissage : ...
    // - Intérêts éducatifs : ...
    // - Intérêts personnels : ...
    // - Traits de personnalité : ...
    
    // Extraer método de aprendizaje
    const methodMatch = insights.match(/Méthode d'apprentissage\s*[:\-]\s*(.+?)(?:\n|$)/i);
    if (methodMatch) {
      const newMethod = methodMatch[1].trim();
      if (newMethod && newMethod !== char.learning_method) {
        // Si cambia significativamente, registrar en metadata
        if (char.learning_method) {
          if (!metadata.significant_changes) {
            metadata.significant_changes = [];
          }
          metadata.significant_changes.push({
            date: new Date().toISOString(),
            field: 'learning_method',
            old_value: char.learning_method,
            new_value: newMethod,
          });
        }
        char.learning_method = newMethod;
      }
    }
    
    // Extraer intereses educativos
    const educInterestsMatch = insights.match(/Intérêts éducatifs\s*[:\-]\s*(.+?)(?:\n|Intérêts personnels|Traits)/is);
    if (educInterestsMatch) {
      const interestsText = educInterestsMatch[1].trim();
      // Dividir por comas, puntos, o saltos de línea
      const interests = interestsText
        .split(/[,;•\n]/)
        .map(i => i.trim())
        .filter(i => i.length > 0);
      
      if (interests.length > 0) {
        const existingInterests = char.educational_interests || [];
        // Agregar nuevos intereses que no existan
        interests.forEach(interest => {
          if (!existingInterests.includes(interest)) {
            existingInterests.push(interest);
          }
        });
        char.educational_interests = existingInterests;
      }
    }
    
    // Extraer intereses personales
    const personalInterestsMatch = insights.match(/Intérêts personnels\s*[:\-]\s*(.+?)(?:\n|Traits|$)/is);
    if (personalInterestsMatch) {
      const interestsText = personalInterestsMatch[1].trim();
      const interests = interestsText
        .split(/[,;•\n]/)
        .map(i => i.trim())
        .filter(i => i.length > 0);
      
      if (interests.length > 0) {
        const existingInterests = char.personal_interests || [];
        interests.forEach(interest => {
          if (!existingInterests.includes(interest)) {
            existingInterests.push(interest);
          }
        });
        char.personal_interests = existingInterests;
      }
    }
    
    // Extraer rasgos de personalidad
    const traitsMatch = insights.match(/Traits de personnalité\s*[:\-]\s*(.+?)(?:\n|$)/is);
    if (traitsMatch) {
      const traitsText = traitsMatch[1].trim();
      const traits = traitsText
        .split(/[,;•\n]/)
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      if (traits.length > 0) {
        const existingTraits = char.personality_traits || [];
        traits.forEach(trait => {
          if (!existingTraits.includes(trait)) {
            existingTraits.push(trait);
          }
        });
        char.personality_traits = existingTraits;
      }
    }
    
    // Actualizar metadatos
    metadata.last_updated = new Date().toISOString();
    if (!metadata.first_insights_date) {
      metadata.first_insights_date = new Date().toISOString();
    }
    metadata.insights_update_count = (metadata.insights_update_count || 0) + 1;
    
    return {
      ...char,
      _metadata: metadata,
    };
  }
  
  /**
   * Actualizar un campo específico en la caracterización del usuario
   * Permite agregar campos parametrizables dinámicamente
   * Ejemplo: updateCharacterizationField('color_favorito', 'azul')
   */
  async updateCharacterizationField(field: string, value: any): Promise<void> {
    try {
      const config = configService.getConfig();
      if (!config.email) {
        return;
      }
      
      const summary = await supabaseService.getUserSummary(config.email);
      if (!summary) {
        return;
      }
      
      const currentChar = summary.user_characterization || {};
      const metadata = currentChar._metadata || {};
      
      // Registrar cambio si el valor es diferente
      if (currentChar[field] !== undefined && currentChar[field] !== value) {
        if (!metadata.significant_changes) {
          metadata.significant_changes = [];
        }
        metadata.significant_changes.push({
          date: new Date().toISOString(),
          field: field,
          old_value: currentChar[field],
          new_value: value,
        });
      }
      
      // Actualizar campo
      const updatedChar: UserCharacterization = {
        ...currentChar,
        [field]: value,
        _metadata: {
          ...metadata,
          last_updated: new Date().toISOString(),
        },
      };
      
      // Guardar en Supabase
      await supabaseService.saveOrUpdateUserSummary({
        email: config.email,
        user_characterization: updatedChar,
      });
    } catch (error) {
      console.error('[ConversationSaver] Error al actualizar campo de caracterización:', error);
    }
  }
  
  /**
   * Actualizar caracterización del usuario con nuevos insights
   */
  private async updateUserCharacterization(insights: string): Promise<void> {
    try {
      const config = configService.getConfig();
      if (!config.email) {
        return;
      }
      
      // Obtener resumen actual del usuario
      const summary = await supabaseService.getUserSummary(config.email);
      if (!summary) {
        return;
      }
      
      // Consolidar insights
      const updatedChar = await this.consolidateUserCharacterization(
        insights,
        summary.user_characterization
      );
      
      // Actualizar en Supabase
      await supabaseService.saveOrUpdateUserSummary({
        email: config.email,
        user_characterization: updatedChar,
      });
    } catch (error) {
      console.error('[ConversationSaver] Error al actualizar caracterización:', error);
    }
  }
  
  /**
   * LambdaCall: Enviar datos usando sendBeacon con fallback a XMLHttpRequest
   * Basado en LambdaCall.jslib
   */
  // @ts-ignore - Función no usada actualmente
  private lambdaCall(url: string, data: string): void {
    const enableLogging = true;
    
    function log(message: string, isError: boolean = false): void {
      if (enableLogging) {
        if (isError) {
          console.error(message);
        } else {
        }
      }
    }
    
    log(`Enviando datos a URL: ${url}`);
    log(`Datos: ${data.substring(0, 100)}...`);
    
    const sending = sessionStorage.getItem('sending');
    
    // Intentar usar sendBeacon primero (más confiable para cierre de navegador)
    if (navigator.sendBeacon && !sending) {
      sessionStorage.setItem('sending', '1');
      const blob = new Blob([data], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        log('Datos enviados exitosamente usando sendBeacon.');
        sessionStorage.removeItem('sending');
      } else {
        log('Error al enviar datos usando sendBeacon, usando XMLHttpRequest como fallback...', true);
        sessionStorage.removeItem('sending');
        this.sendWithXMLHttpRequest(url, data);
      }
    } else {
      log('sendBeacon no disponible o ya hay un envío en curso, usando XMLHttpRequest...');
      this.sendWithXMLHttpRequest(url, data);
    }
  }
  
  /**
   * Fallback: Enviar usando XMLHttpRequest
   */
  private sendWithXMLHttpRequest(url: string, data: string): void {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        console.error('Error al enviar datos:', xhr.status, xhr.statusText);
      }
    };
    
    xhr.onerror = () => {
      console.error('Error de red al enviar datos.');
    };
    
    xhr.send(data);
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export const conversationSaverService = new ConversationSaverService();
export default conversationSaverService;


